import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FileEdit, Search } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PublicationStatusBadge } from "@/components/catalog/PublicationStatusBadge";
import { catalogApi, type CatalogWorkflowRecord } from "@/lib/api/catalog";
import { CatalogDraftDeleteButton } from "./CatalogDraftDeleteButton";
import { useAccessControl } from "@/hooks/useAccessControl";
import { useToast } from "@/contexts/ToastContext";

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

function creatorName(record: CatalogWorkflowRecord): string {
  const creator = record.createdBy;
  if (!creator) return "—";
  const fullName = [creator.firstName, creator.lastName].filter(Boolean).join(" ");
  return fullName || creator.email || "—";
}

export default function CatalogDraftsPage() {
  const toast = useToast();
  const { hasPermission } = useAccessControl();
  const canViewAll = hasPermission("catalog.view") || hasPermission("catalog.edit");
  const [records, setRecords] = useState<CatalogWorkflowRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const loadDrafts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await catalogApi.listDrafts();
      setRecords(response.data);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        "Failed to load drafts.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadDrafts();
  }, [loadDrafts]);

  const filteredRecords = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return records;
    return records.filter((record) => {
      const haystack = [
        record.title,
        record.isbn,
        record.issn,
        creatorName(record),
        record.authors?.join(" ") ?? "",
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [records, searchQuery]);

  const columns = useMemo<DataTableColumn<CatalogWorkflowRecord>[]>(() => {
    const base: DataTableColumn<CatalogWorkflowRecord>[] = [
      {
        id: "title",
        header: "Title",
        cell: (record) => (
          <div>
            <p className="font-medium text-gray-900">{record.title}</p>
            <p className="text-xs text-gray-500">{record.authors?.join(", ") || "No authors"}</p>
          </div>
        ),
      },
      {
        id: "status",
        header: "Status",
        cell: (record) => <PublicationStatusBadge status={record.publicationStatus} />,
      },
      {
        id: "updatedAt",
        header: "Updated",
        cell: (record) => formatDate(record.updatedAt),
      },
      {
        id: "actions",
        header: "",
        align: "right",
        cell: (record) => (
          <div className="flex justify-end gap-2">
            <Button asChild variant="outline" size="sm">
              <Link
                to={
                  record.catalogMethod === "JOURNAL"
                    ? `/catalog/journal/draft/${record.id}`
                    : `/catalog/physical/${record.id}/edit`
                }
              >
                {record.publicationStatus === "REJECTED" ? "View" : "Edit"}
              </Link>
            </Button>
            <CatalogDraftDeleteButton id={record.id} onDeleted={loadDrafts} />
          </div>
        ),
      },
    ];

    if (canViewAll) {
      base.splice(3, 0, {
        id: "createdBy",
        header: "Created by",
        cell: (record) => creatorName(record),
      });
    }

    return base;
  }, [canViewAll, loadDrafts]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Catalog Drafts"
        description={
          canViewAll
            ? "Draft and returned records across your library."
            : "Your draft and returned catalog records."
        }
        showSearch={false}
      />

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <Input
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search by title, author, or ISBN..."
          className="pl-9"
        />
      </div>

      <DataTable
        columns={columns}
        data={filteredRecords}
        rowKey={(record) => record.id}
        loading={loading}
        emptyState={
          <EmptyState
            icon={<FileEdit className="h-6 w-6 text-slate-400" />}
            title={
              searchQuery.trim() && records.length > 0 ? "No matching drafts" : "No drafts found"
            }
            description={
              searchQuery.trim() && records.length > 0
                ? "Try adjusting your search terms."
                : "Save catalog work as a draft or return a submission to see it here."
            }
          />
        }
      />
    </div>
  );
}
